import type { CampaignSelectionEntry } from 'hooks/useLocationParams';
import { orderByIds } from './orderByIds';
import type { CampaignEntry } from '../api';

export interface RestoredCampaignSelection {
  selectedCampaigns: CampaignEntry[];
  selectedCampaignsBudgets: Record<string, string>;
}

/**
 * Restores a deep-linked/shared campaign selection once real campaign rows
 * are available (they're fetched asynchronously, so this can't happen at
 * form-default time like the pack-based creatives restore). Ids that no
 * longer exist among the fetched rows are dropped; budget is only restored
 * for CBO campaigns, matching what the budget column actually persists.
 */
export const restoreSelectedCampaignsFromUrl = (
  entries: readonly CampaignSelectionEntry[],
  rows: readonly CampaignEntry[],
): RestoredCampaignSelection => {
  const entryById = new Map(entries.map((entry) => [entry.id, entry]));
  const selectedCampaigns: CampaignEntry[] = [];
  const selectedCampaignsBudgets: Record<string, string> = {};

  const orderedRows = orderByIds(
    rows.filter((row) => entryById.has(String(row.id))),
    entries.map(({ id }) => id),
  );

  orderedRows.forEach((row) => {
    const entry = entryById.get(String(row.id));
    if (!entry) return;

    selectedCampaigns.push(entry.status ? { ...row, status: entry.status } : row);

    if (row.is_cbo && entry.budget) {
      selectedCampaignsBudgets[row.id] = entry.budget;
    }
  });

  return { selectedCampaigns, selectedCampaignsBudgets };
};

/** Mirrors the current selection (+ CBO budgets) back into the URL's shape. */
export const toCampaignSelectionEntries = (
  rows: readonly CampaignEntry[],
  budgets: Record<string, string> | undefined,
): CampaignSelectionEntry[] =>
  rows.map((row) => ({
    id: String(row.id),
    budget: budgets?.[row.id],
    status: row.status,
  }));
